/**
 * ADL API Module
 * CCH Care — Phase 3
 *
 * Thin wrappers around the ADL PHP endpoints.
 * Auth headers (JWT, membership, tenant, facility) are
 * injected automatically by client.js interceptors.
 *
 * 2026-05-29 — Per-shift N/A:
 *   The four patient_adl_flags wrappers now accept an optional
 *   shiftType argument ('AM' | 'PM' | 'NOC' | null). null/omitted
 *   means "applies to all shifts" — the previous per-section
 *   semantics, stored server-side as the 'ALL' sentinel. Specific
 *   shift values create a (patient, section, shift)-scoped flag that
 *   overrides ALL for that shift only.
 */

import client from './client';

const BASE = '/adl';

// ── Records ─────────────────────────────────────────────────

/**
 * Start or retrieve an existing ADL record for a resident/shift.
 * Idempotent — returns existing record if already created.
 */
export async function createADLRecord(patientId, careShiftId, shiftType) {
  const response = await client.post(`${BASE}/records.php?action=create`, {
    patient_id: patientId,
    care_shift_id: careShiftId,
    shift_type: shiftType,
  });
  return response.data;
}

/**
 * Get a full ADL record with all sections and signatures.
 */
export async function getADLRecord(adlRecordId) {
  const response = await client.get(`${BASE}/records.php`, {
    params: { action: 'get', adl_record_id: adlRecordId },
  });
  return response.data;
}

/**
 * List ADL records for a resident or shift.
 */
export async function listADLRecords({ patientId, careShiftId, shiftDate } = {}) {
  const response = await client.get(`${BASE}/records.php`, {
    params: {
      action: 'list',
      ...(patientId    && { patient_id: patientId }),
      ...(careShiftId  && { care_shift_id: careShiftId }),
      ...(shiftDate    && { shift_date: shiftDate }),
    },
  });
  return response.data;
}

// ── Residents ────────────────────────────────────────────────

/**
 * Get residents assigned to a shift with their ADL completion status.
 * If no careShiftId provided, returns all active residents for the tenant.
 */
export async function getAssignedResidents(careShiftId = null, shiftType = null) {
  const response = await client.get(`${BASE}/residents.php`, {
    params: {
      action: 'assigned',
      ...(careShiftId && { care_shift_id: careShiftId }),
      ...(shiftType   && { shift_type: shiftType }),
    },
  });
  return response.data;
}

/**
 * Search residents by name or patient number.
 * Used by the standalone /adl route resident picker.
 */
export async function searchResidents(query) {
  const response = await client.get(`${BASE}/residents.php`, {
    params: { action: 'search', q: query },
  });
  return response.data;
}

/**
 * Get all active residents for the facility regardless of shift assignment.
 * Used by ADLResidentSelector to populate the "Other Residents" section.
 */
export async function getAllResidents() {
  const response = await client.get(`${BASE}/residents.php`, {
    params: { action: 'all' },
  });
  return response.data;
}

// ── Sections ─────────────────────────────────────────────────

/**
 * Save hygiene entries (array of task rows).
 * entries: [{ task_type, assistance_level, notes }]
 */
export async function saveHygiene(adlRecordId, entries) {
  const response = await client.post(`${BASE}/sections.php?action=hygiene`, {
    adl_record_id: adlRecordId,
    entries,
  });
  return response.data;
}

/**
 * Save a single meal entry. Call once per meal_type.
 */
export async function saveMeal(adlRecordId, mealData) {
  const response = await client.post(`${BASE}/sections.php?action=meals`, {
    adl_record_id: adlRecordId,
    ...mealData,
  });
  return response.data;
}

/**
 * Signs an individual meal entry (breakfast, lunch, or dinner).
 * When all three core meals are signed, the backend automatically
 * creates the section-level adl_signatures record for 'meals'.
 */
export async function signMeal(adlRecordId, mealType) {
  const response = await client.post(`${BASE}/sections.php?action=sign_meal`, {
    adl_record_id: adlRecordId,
    meal_type:     mealType,
  });
  return response.data;
}

/**
 * Save a repositioning event (append-only).
 * Include adl_repo_id to update an existing row.
 */
export async function saveRepositioning(adlRecordId, repoData) {
  const response = await client.post(`${BASE}/sections.php?action=repositioning`, {
    adl_record_id: adlRecordId,
    ...repoData,
  });
  return response.data;
}

/**
 * Save a fluid intake entry (append-only).
 * Include adl_fluid_id to update an existing entry.
 * Returns updated total_ml for the shift.
 */
export async function saveFluidIntake(adlRecordId, fluidData) {
  const response = await client.post(`${BASE}/sections.php?action=fluid-intake`, {
    adl_record_id: adlRecordId,
    ...fluidData,
  });
  return response.data;
}

/**
 * Save bowel movement data (upsert — one row per record).
 */
export async function saveBowel(adlRecordId, bowelData) {
  const response = await client.post(`${BASE}/sections.php?action=bowel`, {
    adl_record_id: adlRecordId,
    ...bowelData,
  });
  return response.data;
}

/**
 * Save urination data (upsert — one row per record).
 */
export async function saveUrination(adlRecordId, urinationData) {
  const response = await client.post(`${BASE}/sections.php?action=urination`, {
    adl_record_id: adlRecordId,
    ...urinationData,
  });
  return response.data;
}

/**
 * Save a skin check finding (append-only).
 * Include adl_skin_check_id to update an existing row.
 */
export async function saveSkinCheck(adlRecordId, skinData) {
  const response = await client.post(`${BASE}/sections.php?action=skin-checks`, {
    adl_record_id: adlRecordId,
    ...skinData,
  });
  return response.data;
}

/**
 * Save safety checklist items in bulk.
 * items: [{ checklist_item_id, is_checked, notes }]
 */
export async function saveSafetyChecks(adlRecordId, items) {
  const response = await client.post(`${BASE}/sections.php?action=safety-checks`, {
    adl_record_id: adlRecordId,
    items,
  });
  return response.data;
}

/**
 * Get safety checklist items for a facility.
 */
export async function getSafetyChecklist(facilityId = null) {
  const response = await client.get(`${BASE}/sections.php`, {
    params: {
      action: 'checklist',
      ...(facilityId && { facility_id: facilityId }),
    },
  });
  return response.data;
}

/**
 * Get all sections of an ADL record in one call (used by section forms).
 */
export async function getADLSectionsForRecord(adlRecordId) {
  const response = await client.get(`${BASE}/sections-for-record.php`, {
    params: { adl_record_id: adlRecordId },
  });
  return response.data;
}

// ── Signatures ───────────────────────────────────────────────

/**
 * CNA signs a completed section.
 * section: 'hygiene' | 'meals' | 'repositioning' | 'fluid_intake'
 *        | 'bowel' | 'urination' | 'skin_checks' | 'safety_checks'
 */
export async function signSection(adlRecordId, section, notes = null) {
  const response = await client.post(`${BASE}/signatures.php?action=sign-section`, {
    adl_record_id: adlRecordId,
    section,
    ...(notes && { notes }),
  });
  return response.data;
}

/**
 * Supervisor co-signs a fully completed ADL record.
 * Record must have all 8 sections CNA-signed first.
 */
export async function supervisorSignRecord(adlRecordId, notes = null) {
  const response = await client.post(`${BASE}/signatures.php?action=sign-supervisor`, {
    adl_record_id: adlRecordId,
    ...(notes && { notes }),
  });
  return response.data;
}

// ── Pending Co-Sign Queue ────────────────────────────────────

/**
 * Get ADL records pending the current supervisor's co-sign.
 * Returns 9/9-signed records that haven't been reviewed yet,
 * scoped to the supervisor's facility / patient scope.
 *
 * @param {number} [days=14] - lookback window, clamped server-side to 1..90
 */
export async function getPendingCoSign(days = 14) {
  const response = await client.get(`${BASE}/pending-cosign.php`, {
    params: { days },
  });
  return response.data;
}

// ── Reopen-Eligible Queue ────────────────────────────────────

/**
 * Get ADL records eligible for supervisor reopen.
 * Returns <9/9 records past the 2-hour grace window but within the
 * 24-hour post-shift reopen cutoff, scoped to the caller's facility /
 * patient scope. Supervisors only — non-supervisors get an empty list.
 *
 * @param {number} [days=2] - lookback window, clamped server-side to 1..7
 */
export async function getReopenEligible(days = 2) {
  const response = await client.get(`${BASE}/reopen-eligible.php`, {
    params: { days },
  });
  return response.data;
}

// ── Amendments ───────────────────────────────────────────────

/**
 * Submit a HIPAA-compliant amendment to a signed section.
 * Creates an audit trail entry then applies the field change.
 */
export async function submitAmendment(adlRecordId, { section, sectionRowId, fieldName, oldValue, newValue, reason }) {
  const response = await client.post(`${BASE}/signatures.php?action=amend`, {
    adl_record_id:   adlRecordId,
    section,
    section_row_id:  sectionRowId,
    field_name:      fieldName,
    old_value:       oldValue,
    new_value:       newValue,
    reason,
  });
  return response.data;
}

/**
 * Get all amendments for an ADL record.
 */
export async function getAmendments(adlRecordId) {
  const response = await client.get(`${BASE}/signatures.php`, {
    params: { action: 'amendments', adl_record_id: adlRecordId },
  });
  return response.data;
}

// ── Vitals ───────────────────────────────────────────────────

/**
 * Get all vitals readings + signatures for an ADL record.
 */
export async function getVitals(adlRecordId) {
  const response = await client.get(`${BASE}/vitals.php`, {
    params: { action: 'get', adl_record_id: adlRecordId },
  });
  return response.data;
}

/**
 * Save a new vitals reading (section must be unsigned).
 * vitalsData: { systolic, diastolic, pulse, temperature, o2_saturation, respiratory_rate, recorded_at, notes }
 */
export async function saveVitals(adlRecordId, vitalsData) {
  const response = await client.post(`${BASE}/vitals.php?action=save`, {
    adl_record_id: adlRecordId,
    ...vitalsData,
  });
  return response.data;
}

/**
 * Update an existing vitals reading (before section is signed).
 */
export async function updateVitals(adlVitalsId, vitalsData) {
  const response = await client.post(`${BASE}/vitals.php?action=update`, {
    adl_vitals_id: adlVitalsId,
    ...vitalsData,
  });
  return response.data;
}

/**
 * Add a vitals reading after the section has been signed (amendment).
 */
export async function amendVitals(adlRecordId, vitalsData) {
  const response = await client.post(`${BASE}/vitals.php?action=amend`, {
    adl_record_id: adlRecordId,
    ...vitalsData,
  });
  return response.data;
}

/**
 * CNA signs the vitals section.
 */
export async function signVitals(adlRecordId) {
  const response = await client.post(`${BASE}/vitals.php?action=sign`, {
    adl_record_id: adlRecordId,
  });
  return response.data;
}

/**
 * Supervisor co-signs the vitals section.
 */
export async function cosignVitals(adlRecordId) {
  const response = await client.post(`${BASE}/vitals.php?action=cosign`, {
    adl_record_id: adlRecordId,
  });
  return response.data;
}

// ── Care Plan Flags ──────────────────────────────────────────

/**
 * Get all ADL section flags for a single resident.
 *
 * Without shiftType:
 *   Returns { flags: [...], flags_by_shift: { section: { shift_key: flag } } }
 *   flags_by_shift is what PatientADLFlagsPanel consumes to render
 *   per-shift pills. shift_key is 'ALL' | 'AM' | 'PM' | 'NOC'.
 *
 * With shiftType ('AM' | 'PM' | 'NOC'):
 *   Adds a `map: { section: flag }` field — resolved-for-this-shift
 *   per the precedence rule (specific > ALL > absent). ADLFlowSheetPage
 *   passes its current shift_type so the same map shape it always
 *   consumed still works.
 *
 * shift_type is surfaced as null on the API for the 'ALL' sentinel.
 */
export async function getPatientADLFlags(patientId, shiftType = null) {
  const response = await client.get(`${BASE}/patient_adl_flags.php`, {
    params: {
      action: 'get',
      patient_id: patientId,
      ...(shiftType && { shift_type: shiftType }),
    },
  });
  return response.data;
}

/**
 * Get ADL flags for multiple patients in one call (shift load).
 *
 * Resolves per-shift when shiftType is provided. Without shiftType,
 * the server returns empty per-patient maps — callers should always
 * pass the current shift_type for dashboard load.
 *
 * Returns { flags_by_patient: { patientId: { section: flagObject } } }
 */
export async function batchGetPatientADLFlags(patientIds, shiftType = null) {
  const response = await client.post(`${BASE}/patient_adl_flags.php?action=batch`, {
    patient_ids: patientIds,
    shift_type:  shiftType,
  });
  return response.data;
}

/**
 * Set (or update) an ADL section flag for a resident on a specific shift.
 * Supervisor/Admin only.
 *
 * shiftType: null/omitted → applies to all shifts ('ALL' sentinel server-side)
 *            'AM' | 'PM' | 'NOC' → applies to that shift only, overriding ALL
 * isApplicable: false = N/A for this resident on this shift
 */
export async function setPatientADLFlag(
  patientId,
  section,
  isApplicable,
  reason = '',
  effectiveDate = null,
  shiftType = null,
) {
  const response = await client.post(`${BASE}/patient_adl_flags.php?action=set`, {
    patient_id:     patientId,
    section,
    is_applicable:  isApplicable,
    reason,
    effective_date: effectiveDate || new Date().toLocaleDateString('en-CA'),
    shift_type:     shiftType,
  });
  return response.data;
}

/**
 * Remove a flag (restores section to applicable on that scope).
 *
 * shiftType: null/omitted → removes the 'all shifts' (ALL) flag
 *            'AM' | 'PM' | 'NOC' → removes only that shift's flag
 *
 * Note: removing a specific shift's flag while an ALL=N/A flag still
 * exists for the same section leaves the section still resolved as N/A
 * for that shift (ALL bleeds through). PatientADLFlagsPanel's
 * "Customize per shift" path handles legacy ALL flags by decomposing
 * them into three specific flags first.
 */
export async function resetPatientADLFlag(patientId, section, shiftType = null) {
  const response = await client.post(`${BASE}/patient_adl_flags.php?action=reset`, {
    patient_id: patientId,
    section,
    shift_type: shiftType,
  });
  return response.data;
}

// ── Section Attestations ─────────────────────────────────────

/**
 * Record "nothing to report" for a section.
 * Used when a section applies but nothing occurred this shift.
 * Sections: bowel, urination, fluid_intake, repositioning, skin_checks, safety_checks
 */
export async function attestNothingToReport(adlRecordId, section, notes = '') {
  const response = await client.post(`${BASE}/attestations.php?action=attest`, {
    adl_record_id: adlRecordId,
    section,
    notes,
  });
  return response.data;
}

/**
 * Retract a nothing-to-report attestation (before section is signed).
 */
export async function retractAttestation(adlRecordId, section) {
  const response = await client.post(`${BASE}/attestations.php?action=retract`, {
    adl_record_id: adlRecordId,
    section,
  });
  return response.data;
}

/**
 * Get all attestations for an ADL record.
 * Returns { attestations: [], map: { section: attestationObject } }
 */
export async function getAttestations(adlRecordId) {
  const response = await client.get(`${BASE}/attestations.php`, {
    params: { action: 'get', adl_record_id: adlRecordId },
  });
  return response.data;
}

// ── Late Documentation Phase 2 ───────────────────────────────

/**
 * Supervisor reopens an ADL record for late documentation.
 * Care Supervisor / Care Admin only. Window: 2-24 hours post-shift.
 */
export async function reopenADLRecord(adlRecordId, reason = null) {
  const response = await client.post(`${BASE}/reopen.php`, {
    adl_record_id: adlRecordId,
    ...(reason && { reason }),
  });
  return response.data;
}